import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/* Company plans — platform admin API.
 *
 * Mirrors packageServices.js exactly (same base query, same token key, same
 * tag-based invalidation) so it behaves like every other service here.
 *
 * Note the base URL: this hits /api/company-plans-admin, NOT /api/admin. The
 * plans admin surface is mounted separately because every one of its routes
 * sits below its own isPriviledged barrier. */
export const companyPlanApi = createApi({
    reducerPath: 'companyPlanApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BACKEND_HOST}/api/company-plans-admin`,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('prona_userToken');
            if (token) headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    tagTypes: ['Companies', 'Company', 'Catalogue', 'Slots', 'Leads', 'Stats', 'Partners', 'Subscription'],
    endpoints: (build) => ({
        getPlanStats: build.query({
            query: () => '/stats',
            providesTags: ['Stats'],
        }),

        /* Which plans sell (Plans & Pricing report). range: 30d | 90d | 12m | all */
        getPlanSales: build.query({
            query: (range = '12m') => `/sales?range=${encodeURIComponent(range)}`,
            providesTags: ['Stats', 'Companies'],
        }),

        getPlanCompanies: build.query({
            query: (params = {}) => {
                const clean = Object.fromEntries(
                    Object.entries(params).filter(([, v]) => v !== '' && v != null)
                );
                const qs = new URLSearchParams(clean).toString();
                return `/companies${qs ? `?${qs}` : ''}`;
            },
            providesTags: ['Companies'],
        }),

        getPlanCompany: build.query({
            query: (id) => `/companies/${id}`,
            providesTags: (result, error, id) => [{ type: 'Company', id }],
        }),

        /* A hand-assigned plan settles outside the payment gateway, so the form
           also sends how it was paid (payment_method / payment_reference /
           paid_at) and, optionally, the paperwork behind it.

           `data` may be a plain object OR a FormData. The route now mounts
           multer (plan_document.upload's .fields for contract + invoice), which
           passes a non-multipart request straight through, so both shapes are
           valid: JSON when there is nothing to attach, FormData when there is.
           fetchBaseQuery only sets a JSON Content-Type for jsonifiable bodies
           and leaves FormData alone for the browser to boundary itself, so
           nothing here has to branch.

           This is the upload that was previously amputated: the route carried
           no multer, so a multipart POST arrived with req.body === {} and the
           grant failed with 400 "Unknown tier.". That is fixed. */
        assignCompanyPlan: build.mutation({
            query: ({ id, data }) => ({
                url: `/companies/${id}/plan`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Company', id },
                'Companies',
                'Slots',
                'Stats',
            ],
        }),

        /* What a tier actually grants, in words, for the assign form's preview.
           A mutation rather than a query because it POSTs the negotiated
           overrides alongside the tier — and because it is fired on every
           change of either, caching it by tier alone would show stale numbers
           the moment an override is typed. */
        previewPlan: build.mutation({
            query: (body) => ({ url: '/plan-preview', method: 'POST', body }),
        }),

        /* Freeze / unfreeze. Both invalidate the same tags as assigning, since
           both change what the company is currently entitled to. */
        suspendCompanyPlan: build.mutation({
            query: ({ id, reason }) => ({
                url: `/companies/${id}/plan/suspend`,
                method: 'POST',
                body: { reason },
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Company', id },
                'Companies',
                'Slots',
                'Stats',
            ],
        }),

        resumeCompanyPlan: build.mutation({
            query: ({ id }) => ({
                url: `/companies/${id}/plan/resume`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Company', id },
                'Companies',
                'Slots',
                'Stats',
            ],
        }),

        /* Attaches paperwork to a plan that ALREADY exists. Separate from the
           assign mutation because the signed copy and the invoice usually
           arrive after the sale — and re-assigning to file them would cancel
           the current row and restart the term. `data` is always a FormData. */
        uploadPlanDocuments: build.mutation({
            query: ({ id, data }) => ({
                url: `/companies/${id}/plan/documents`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }],
        }),

        /* Renders the agreement server-side and stamps it on the plan row, so
           the Company tag has to refresh for the new contract_file to show. */
        generatePlanContract: build.mutation({
            query: ({ id }) => ({
                url: `/companies/${id}/plan/contract`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }],
        }),

        revokeCompanyPlan: build.mutation({
            query: (id) => ({ url: `/companies/${id}/plan`, method: 'DELETE' }),
            invalidatesTags: (result, error, id) => [
                { type: 'Company', id },
                'Companies',
                'Slots',
                'Stats',
            ],
        }),

        getAllTopSlots: build.query({
            query: (params = {}) => {
                const qs = new URLSearchParams(params).toString();
                return `/top-slots${qs ? `?${qs}` : ''}`;
            },
            providesTags: ['Slots'],
        }),

        getPlanFavourites: build.query({
            query: (params = {}) => {
                const qs = new URLSearchParams(params).toString();
                return `/favourites${qs ? `?${qs}` : ''}`;
            },
            providesTags: ['Slots'],
        }),

        setListingFavourite: build.mutation({
            query: ({ listingId, is_favourite }) => ({
                url: `/listings/${listingId}/favourite`,
                method: 'PUT',
                body: { is_favourite },
            }),
            invalidatesTags: ['Slots'],
        }),

        getPlanCatalogue: build.query({
            query: () => '/catalogue',
            providesTags: ['Catalogue'],
        }),

        /* One plan for the plan editor (/plans/:id/edit): the tier with its
           features, its terms and what assignPlan actually grants. `ref` is the
           tier id or its key. */
        getPlanTier: build.query({
            query: (ref) => `/catalogue/tiers/${encodeURIComponent(ref)}`,
            providesTags: ['Catalogue'],
        }),

        /* The editor sends the whole plan: texts in three languages, settings,
           limits, and — when present — the complete `features` list and the
           `terms`, saved in one transaction. The older catalogue modal still
           sends tier fields only, which leaves features and terms alone. */
        updatePlanTier: build.mutation({
            query: ({ tier, data }) => ({
                url: `/catalogue/tiers/${tier}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Catalogue'],
        }),

        createPlanTerm: build.mutation({
            query: (data) => ({ url: '/catalogue/terms', method: 'POST', body: data }),
            invalidatesTags: ['Catalogue'],
        }),

        updatePlanTerm: build.mutation({
            query: ({ id, data }) => ({
                url: `/catalogue/terms/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Catalogue'],
        }),

        /* Soft-disables the term on the server — a subscription somebody bought
           still references it. */
        deletePlanTerm: build.mutation({
            query: (id) => ({ url: `/catalogue/terms/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Catalogue'],
        }),

        /* Tier feature bullets. The same three verbs the packages screen uses,
           against /catalogue rather than /packages, and all three invalidate
           'Catalogue' so the tier they belong to redraws with them. */
        addTierFeature: build.mutation({
            query: ({ tier, data }) => ({
                url: `/catalogue/tiers/${tier}/features`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Catalogue'],
        }),

        updateTierFeature: build.mutation({
            query: ({ id, data }) => ({
                url: `/catalogue/features/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Catalogue'],
        }),

        deleteTierFeature: build.mutation({
            query: (id) => ({ url: `/catalogue/features/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Catalogue'],
        }),

        getPlanLeads: build.query({
            query: (params = {}) => {
                const clean = Object.fromEntries(
                    Object.entries(params).filter(([, v]) => v !== '' && v != null)
                );
                const qs = new URLSearchParams(clean).toString();
                return `/leads${qs ? `?${qs}` : ''}`;
            },
            providesTags: ['Leads'],
        }),

        updatePlanLead: build.mutation({
            query: ({ id, data }) => ({ url: `/leads/${id}`, method: 'PUT', body: data }),
            invalidatesTags: ['Leads', 'Stats'],
        }),

        /* Homepage partners — every company with an active plan, plus the
           companies and outside partners added by hand. Each is addressed by
           its partner_key ("c<company id>" or "p<partner id>"). */
        getPartners: build.query({
            query: () => '/partners',
            providesTags: ['Partners'],
        }),

        /* `data` is a FormData when a logo is attached (field `logo`), a plain
           object otherwise: { company_id } or { name }, plus link_url and
           is_visible. */
        createPartner: build.mutation({
            query: (data) => ({ url: '/partners', method: 'POST', body: data }),
            invalidatesTags: ['Partners'],
        }),

        /* Same body shapes; { remove_logo: true } drops a custom logo. */
        updatePartner: build.mutation({
            query: ({ key, data }) => ({
                url: `/partners/${encodeURIComponent(key)}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Partners'],
        }),

        deletePartner: build.mutation({
            query: (key) => ({ url: `/partners/${encodeURIComponent(key)}`, method: 'DELETE' }),
            invalidatesTags: ['Partners'],
        }),

        /* { partner_keys: [...] } — the full order — or { reset: true }. */
        reorderPartners: build.mutation({
            query: (body) => ({ url: '/partners/order', method: 'PUT', body }),
            invalidatesTags: ['Partners'],
        }),

        /* ---------------------------------------------------------------------
           Company Subscriptions — per-agency slots and team (2026-10-26).
           companySubscriptionsAdmin.controller.js on the backend. Every write
           goes through the same models the agency's own panel uses, so the
           change is already in the agency's account when these resolve. */
        getCompanySubscription: build.query({
            query: (id) => `/companies/${id}/subscription`,
            providesTags: (result, error, id) => [{ type: 'Subscription', id }],
        }),
        updateSlotAllowance: build.mutation({
            query: ({ id, ...body }) => ({ url: `/companies/${id}/slot-allowance`, method: 'PUT', body }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Subscription', id }, { type: 'Company', id }, 'Companies', 'Slots', 'Stats',
            ],
        }),
        assignCompanyTopSlot: build.mutation({
            query: ({ id, slotIndex, listing_id }) => ({
                url: `/companies/${id}/top-slots/${slotIndex}`, method: 'PUT', body: { listing_id },
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Subscription', id }, { type: 'Company', id }, 'Slots', 'Stats',
            ],
        }),
        clearCompanyTopSlot: build.mutation({
            query: ({ id, slotIndex }) => ({ url: `/companies/${id}/top-slots/${slotIndex}`, method: 'DELETE' }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Subscription', id }, { type: 'Company', id }, 'Slots', 'Stats',
            ],
        }),
        setCompanySlotFavourite: build.mutation({
            query: ({ id, slotIndex, is_favourite }) => ({
                url: `/companies/${id}/top-slots/${slotIndex}/favourite`, method: 'PUT', body: { is_favourite },
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Subscription', id }, { type: 'Company', id }, 'Slots', 'Stats',
            ],
        }),
        assignCompanyExclusiveSlot: build.mutation({
            query: ({ id, slotIndex, listing_id, approve = true }) => ({
                url: `/companies/${id}/exclusive-slots/${slotIndex}`, method: 'PUT', body: { listing_id, approve },
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Subscription', id }, { type: 'Company', id }, 'Stats',
            ],
        }),
        clearCompanyExclusiveSlot: build.mutation({
            query: ({ id, slotIndex }) => ({ url: `/companies/${id}/exclusive-slots/${slotIndex}`, method: 'DELETE' }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Subscription', id }, { type: 'Company', id }, 'Stats',
            ],
        }),
        createCompanyAgent: build.mutation({
            query: ({ id, ...body }) => ({ url: `/companies/${id}/agents`, method: 'POST', body }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Subscription', id }, { type: 'Company', id }],
        }),
        setCompanyAgentStatus: build.mutation({
            query: ({ id, agentId, status }) => ({
                url: `/companies/${id}/agents/${agentId}/status`, method: 'PUT', body: { status },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Subscription', id }, { type: 'Company', id }],
        }),
        resendCompanyAgentInvite: build.mutation({
            query: ({ id, agentId }) => ({ url: `/companies/${id}/agents/${agentId}/resend-invite`, method: 'POST' }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Subscription', id }],
        }),
        removeCompanyAgent: build.mutation({
            query: ({ id, agentId, reassign_to }) => ({
                url: `/companies/${id}/agents/${agentId}`, method: 'DELETE', body: { reassign_to: reassign_to || null },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Subscription', id }, { type: 'Company', id }],
        }),
        restoreCompanyAgent: build.mutation({
            query: ({ id, agentId }) => ({ url: `/companies/${id}/agents/${agentId}/restore`, method: 'POST' }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Subscription', id }, { type: 'Company', id }],
        }),
    }),
});

export const {
    useGetPlanStatsQuery,
    useGetPlanSalesQuery,
    useGetPlanCompaniesQuery,
    useGetPlanCompanyQuery,
    useAssignCompanyPlanMutation,
    usePreviewPlanMutation,
    useSuspendCompanyPlanMutation,
    useResumeCompanyPlanMutation,
    useGeneratePlanContractMutation,
    useUploadPlanDocumentsMutation,
    useRevokeCompanyPlanMutation,
    useGetAllTopSlotsQuery,
    useGetPlanFavouritesQuery,
    useSetListingFavouriteMutation,
    useGetPlanCatalogueQuery,
    useGetPlanTierQuery,
    useUpdatePlanTierMutation,
    useCreatePlanTermMutation,
    useUpdatePlanTermMutation,
    useDeletePlanTermMutation,
    useAddTierFeatureMutation,
    useUpdateTierFeatureMutation,
    useDeleteTierFeatureMutation,
    useGetPlanLeadsQuery,
    useUpdatePlanLeadMutation,
    useGetPartnersQuery,
    useCreatePartnerMutation,
    useUpdatePartnerMutation,
    useDeletePartnerMutation,
    useReorderPartnersMutation,
    useGetCompanySubscriptionQuery,
    useUpdateSlotAllowanceMutation,
    useAssignCompanyTopSlotMutation,
    useClearCompanyTopSlotMutation,
    useSetCompanySlotFavouriteMutation,
    useAssignCompanyExclusiveSlotMutation,
    useClearCompanyExclusiveSlotMutation,
    useCreateCompanyAgentMutation,
    useSetCompanyAgentStatusMutation,
    useResendCompanyAgentInviteMutation,
    useRemoveCompanyAgentMutation,
    useRestoreCompanyAgentMutation,
} = companyPlanApi;
